import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import PreviewIcon from '@mui/icons-material/Preview';
import { Box, Button, Divider, Drawer, Stack, Typography } from '@mui/material';
import { type FC, type ReactNode, useEffect, useState } from 'react';

import { useDisplayTimeZone } from '@hooks/useDisplayTimeZone';
import { useSettings } from '@hooks/useSettings';
import { useUpdateSettingsUser } from '@hooks/useUpdateSettingsUser';
import { ClickableBox } from '@uiKit/ClickableBox';
import { DETAILS, LOCAL_TIME_ZONE, MAILROOM_TIME_ZONE, PREFERRED_TIME_ZONE, PREVIEW, TABLE_IMAGE } from 'src/constants';

import { drawerPaperSx, optionBoxSx } from './styles';

type ImageBehavior = typeof PREVIEW | typeof DETAILS;
type TimeZonePreference = typeof LOCAL_TIME_ZONE | typeof MAILROOM_TIME_ZONE;

const SETTING_NAMES = [TABLE_IMAGE, PREFERRED_TIME_ZONE];

interface SettingOptionProps {
  title: string;
  description: ReactNode;
  selected: boolean;
  onSelect: () => void;
  icon?: ReactNode;
}

const SettingOption = ({ title, description, selected, onSelect, icon }: SettingOptionProps) => (
  <ClickableBox role="button" tabIndex={0} onClick={onSelect} sx={optionBoxSx(selected)}>
    {icon}
    <Box>
      <Typography variant="subtitle1">{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </ClickableBox>
);

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const SettingsDrawer: FC<SettingsDrawerProps> = ({ open, onClose }) => {
  const settings = useSettings(SETTING_NAMES) as Record<string, string>;
  const { updateSettingsUser, isLoading } = useUpdateSettingsUser();
  const { browserTimeZone, mailroomTimeZone } = useDisplayTimeZone();

  const savedValue = (settings[TABLE_IMAGE] as ImageBehavior) || PREVIEW;
  const [imageBehavior, setImageBehavior] = useState<ImageBehavior>(savedValue);

  const savedTimeZone: TimeZonePreference =
    settings[PREFERRED_TIME_ZONE] === MAILROOM_TIME_ZONE ? MAILROOM_TIME_ZONE : LOCAL_TIME_ZONE;
  const [timeZonePreference, setTimeZonePreference] = useState<TimeZonePreference>(savedTimeZone);

  useEffect(() => {
    if (open) {
      setImageBehavior(savedValue);
      setTimeZonePreference(savedTimeZone);
    }
  }, [open, savedValue, savedTimeZone]);

  const isDirty = imageBehavior !== savedValue || timeZonePreference !== savedTimeZone;

  const handleSave = async () => {
    await updateSettingsUser(SETTING_NAMES, {
      [TABLE_IMAGE]: imageBehavior,
      [PREFERRED_TIME_ZONE]: timeZonePreference,
    });
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: drawerPaperSx } }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">Settings</Typography>
      </Box>

      <Divider />

      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, mt: 1 }}>
          Date and Time Display
        </Typography>

        <Stack spacing={1.5}>
          <SettingOption
            title="Local time zone"
            description={`Display all dates and times using my local time zone (${browserTimeZone})`}
            selected={timeZonePreference === LOCAL_TIME_ZONE}
            onSelect={() => setTimeZonePreference(LOCAL_TIME_ZONE)}
          />

          <SettingOption
            title="Mailroom time zone"
            description={`Display all dates and times using the mailroom’s configured time zone${
              mailroomTimeZone ? ` (${mailroomTimeZone})` : ''
            }`}
            selected={timeZonePreference === MAILROOM_TIME_ZONE}
            onSelect={() => setTimeZonePreference(MAILROOM_TIME_ZONE)}
          />
        </Stack>

        <Typography variant="subtitle1" sx={{ mb: 2, mt: 4 }}>
          Table Image Behavior
        </Typography>

        <Stack spacing={1.5}>
          <SettingOption
            icon={<ImageSearchIcon sx={{ color: 'text.primary' }} />}
            title="Image preview"
            description="Clicking a table image opens a preview of the image"
            selected={imageBehavior === PREVIEW}
            onSelect={() => setImageBehavior(PREVIEW)}
          />

          <SettingOption
            icon={<PreviewIcon sx={{ color: 'text.primary' }} />}
            title="Item Details page"
            description="Clicking a table image opens the Item Details page"
            selected={imageBehavior === DETAILS}
            onSelect={() => setImageBehavior(DETAILS)}
          />
        </Stack>
      </Box>

      <Box sx={{ mt: 'auto', p: 2 }}>
        <Button
          size="large"
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSave}
          disabled={!isDirty || isLoading}
        >
          Save Changes
        </Button>
      </Box>
    </Drawer>
  );
};
